import React, { useState } from "react";
import axios from "axios";
import "../styles/DriverSidebar.css";

const DriverSidebar = ({
  className = "",
  drivers,
  isVisible,
  setIsVisible,
  fetchDrivers,
  onDriverClick,
  updateDriverOptimistically,
}) => {
  const [activeTab, setActiveTab] = useState("free");
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchActive, setIsSearchActive] = useState(false);

  const API_KEY = "b6c8b023-5992-4d5a-a366-265a6703c3fc";
  const EMAIL_DISPATCH = "dispatch@lalivraison.ma";

  const tabs = [
    { id: "free", label: "FREE", count: drivers.free.length },
    { id: "busy", label: "BUSY", count: drivers.busy.length },
    { id: "externe", label: "EXTERNE", count: drivers.externe.length },
    { id: "inactive", label: "INACTIVE", count: drivers.inactive.length },
  ];

  const extractDriverInfo = (driver) => {
    let name = driver.nom || "Livreur Inconnu";
    let balance = driver.solde_livreur ?? "N/A";

    if (!driver.solde_livreur) {
      const balanceMatch = name.match(/(.+?)\s*-\s*Solde livreur\s*:\s*(-?\d+)\s*-/);
      if (balanceMatch) {
        name = balanceMatch[1].trim();
        balance = balanceMatch[2];
      } else {
        name = name.replace(/\s*-\s*Solde livreur\s*:\s*-?\d+\s*-/, "").trim();
      }
    }

    return { name, balance };
  };

  const getInitials = (name) => {
    const cleanName = extractDriverInfo({ nom: name }).name;
    if (!cleanName) return "N/A";
    const words = cleanName.split(" ");
    return words.length > 1 ? words[0][0] + words[1][0] : words[0][0] || "N/A";
  };

  const handleConnectToggle = async (driver) => {
    const newConnectedStatus = driver.connected == 1 ? 0 : 1;
    const newStatus = newConnectedStatus == 1 ? "FREE" : "INACTIVE";

    updateDriverOptimistically(driver.uuid, {
      connected: newConnectedStatus,
      status: newStatus,
      lat: driver.lat,
      lng: driver.lng,
    });

    const params = new URLSearchParams();
    params.append("uuid", driver.uuid);
    params.append("connected", newConnectedStatus);
    params.append("email_dispatch", EMAIL_DISPATCH);
    params.append("api_key", API_KEY);

    try {
      const response = await axios.post(
        "https://lalivraison.ma/Livreurxcs/Dispatch/updatelivreur.php",
        params,
        {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          timeout: 15000,
        }
      );

      if (response.data.updateliv == newConnectedStatus) {
        fetchDrivers();
      } else {
        updateDriverOptimistically(driver.uuid, {
          connected: driver.connected,
          status: driver.status,
          lat: driver.lat,
          lng: driver.lng,
        });
        alert("Failed to update driver status");
      }
    } catch (err) {
      updateDriverOptimistically(driver.uuid, {
        connected: driver.connected,
        status: driver.status,
        lat: driver.lat,
        lng: driver.lng,
      });
      alert("Error updating driver: " + err.message);
    }
  };

  const handleRestart = async (driver) => {
    updateDriverOptimistically(driver.uuid, {
      connected: 1,
      status: "FREE",
      lat: driver.lat,
      lng: driver.lng,
      nb_commande: 0,
    });

    const params = new URLSearchParams();
    params.append("uuid", driver.uuid);
    params.append("email_dispatch", EMAIL_DISPATCH);
    params.append("api_key", API_KEY);

    try {
      const response = await axios.post(
        "https://lalivraison.ma/Livreurxcs/Dispatch/reloadlivreur.php",
        params,
        {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          timeout: 15000,
        }
      );

      fetchDrivers();
    } catch (err) {
      updateDriverOptimistically(driver.uuid, {
        connected: driver.connected,
        status: driver.status,
        lat: driver.lat,
        lng: driver.lng,
        nb_commande: driver.nb_commande,
      });
      alert("Error restarting driver: " + err.message);
    }
  };

  const handleDriverAvatarClick = (driver) => {
    if (driver.lat && driver.lng && !isNaN(parseFloat(driver.lat)) && !isNaN(parseFloat(driver.lng))) {
      const driverData = {
        lat: parseFloat(driver.lat),
        lng: parseFloat(driver.lng),
        driverId: driver.id || driver.uuid, // Use uuid as fallback
        driver: driver,
      };
      onDriverClick(driverData);
    } else {
      console.warn(`Cannot center on driver ${driver.name || driver.nom} due to invalid coordinates: Lat=${driver.lat}, Lng=${driver.lng}`);
    }
  };

  const filteredDrivers = drivers[activeTab].filter((driver) =>
    (extractDriverInfo(driver).name.toLowerCase() || "").includes(
      searchTerm.toLowerCase()
    )
  );

  return (
    <div className={`driver-sidebar ${className}`}>
      <h2>Livreurs</h2>
      <div className="driver-controls">
        <div className="search-container">
          {!isSearchActive ? (
            <button
              className="search-toggle"
              onClick={() => setIsSearchActive(true)}
              title="Rechercher"
            >
              <i className="fas fa-search"></i>
            </button>
          ) : (
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onBlur={() => searchTerm === "" && setIsSearchActive(false)}
              autoFocus
              className="search-input"
            />
          )}
        </div>
      </div>
      <div className="filters-content2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`filters__button2 ${activeTab === tab.id ? "is-active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-count">{tab.count}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>
      <div className="driver-content">
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className={`driver-tab ${activeTab === tab.id ? "is-active" : ""}`}
          >
            {filteredDrivers.length > 0 ? (
              filteredDrivers.map((driver) => {
                const { name, balance } = extractDriverInfo(driver);
                return (
                  <div key={driver.id} className="driver-card">
                    <div className="driver-header">
                      <div
                        className="driver-avatar"
                        onClick={() => handleDriverAvatarClick(driver)}
                        style={{ cursor: "pointer" }}
                      >
                        <span>{getInitials(driver.nom)}</span>
                        <div
                          className={`driver-status ${
                            driver.status.toLowerCase() === "free"
                              ? "status-free"
                              : driver.status.toLowerCase() === "busy"
                              ? "status-busy"
                              : "status-inactive"
                          }`}
                        ></div>
                      </div>
                      <div className="driver-title">
                        <h3>{name || "Livreur Inconnu"}</h3>
                        {driver.type && (
                          <span className="driver-type">{driver.type}</span>
                        )}
                      </div>
                      <div className="driver-actions">
                        <button
                          className={`action-button ${
                            driver.connected == 1 ? "disconnect" : "connect"
                          }`}
                          onClick={() => handleConnectToggle(driver)}
                          title={driver.connected == 1 ? "Déconnecter" : "Connecter"}
                        >
                          <i
                            className={`fas ${
                              driver.connected == 1 ? "fa-plug-circle-xmark" : "fa-plug-circle-check"
                            }`}
                          ></i>
                        </button>
                        <button
                          className="action-button restart"
                          onClick={() => handleRestart(driver)}
                          title="Redémarrer"
                        >
                          <i className="fas fa-sync-alt"></i>
                        </button>
                      </div>
                    </div>
                    <div className="driver-details">
                      <div className="driver-info">
                        <i className="fas fa-phone"></i>
                        <span>{driver.telephone || "N/A"}</span>
                      </div>
                      <div className="driver-info">
                        <i className="fas fa-map-marker-alt"></i>
                        <span>
                          {driver.lat && driver.lng
                            ? `${driver.lat}, ${driver.lng}`
                            : "N/A"}
                        </span>
                      </div>
                      <div className="driver-info">
                        <i className="fas fa-box"></i>
                        <span>{driver.nb_commande || 0} Commandes</span>
                      </div>
                      <div className="driver-info">
                        <i className="fas fa-wallet"></i>
                        <span>{balance === "N/A" ? "N/A" : `${balance} MAD`}</span>
                      </div>
                      <div className={`driver-info ${driver.battery && parseInt(driver.battery) <= 20 ? "low-battery" : ""}`}>
                        <i className="fas fa-battery-three-quarters"></i>
                        <span>{driver.battery || "N/A"}%</span>
                      </div>
                      <div className={`driver-info ${driver.last_update && parseInt(driver.last_update) !== 0 ? "outdated-time" : ""}`}>
                        <i className="fas fa-clock"></i>
                        <span>{driver.last_update ? `${driver.last_update} min` : "N/A"}</span>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="no-drivers">Aucun livreur trouvé.</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DriverSidebar;